// SCR-STAFF-926 — گزارش دسترسی به پرونده (AP-IAM-04؛ US-IAM-061، AC-IAM-061.1): فهرست زمانی مشاهده‌ها، ردهای سیاست دید و تغییرات یک
// مراجعه‌کننده با کاربر، نقش، نوع عملیات و دسترسی اضطراری؛ خروجی CSV (Audit می‌شود) و نسخه چاپی تا PDF امضاشده DOC (C-AGENT-403).
// دیدن گزارش خود record.viewed است (iam.audit.view_patient_access).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, ListPage } from '@phoenix/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { downloadBlob, useAuditColumns } from './AuditEventsTable';
import { iamKeys } from './common';

export const SCR_STAFF_926 = 'SCR-STAFF-926';

export function PatientAccessReportPage() {
  const { t } = useTranslation('iam');
  const { patientId = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const columns = useAuditColumns();
  const [exportError, setExportError] = useState<unknown>(undefined);
  const [notice, setNotice] = useState<string | null>(null);

  const report = useInfiniteQuery({
    queryKey: iamKeys.accessReport(session?.tenantId, patientId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/identity/patients/{patientId}/access-report', {
          params: { path: { patientId }, query: { cursor: pageParam, limit: 100 } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: patientId.length > 0,
  });
  const first = report.data?.pages[0];

  const exportCsv = async () => {
    setExportError(undefined);
    const response = await tenantApi.POST('/api/v1/identity/patients/{patientId}/access-report:export', {
      params: { path: { patientId } },
      parseAs: 'blob',
    });
    if (response.response.ok && response.data) {
      downloadBlob(response.data as unknown as Blob, 'patient-access-report.csv');
      setNotice(t('iam:audit.exported'));
    } else {
      setExportError(response.error ?? new Error('export'));
    }
  };

  return (
    <ListPage screenId={SCR_STAFF_926} titleKey="iam:access_report.title">
      <Stack spacing={2}>
        {first && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <span data-testid="access-report-patient">
              {t('iam:access_report.subtitle', { name: first.patientName ?? '', number: faDigits(first.patientFileNumber ?? '') })}
            </span>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => void exportCsv()} data-testid="access-report-export">
                {t('iam:access_report.export')}
              </Button>
              <Button variant="outlined" onClick={() => window.print()} data-testid="access-report-print">
                {t('iam:access_report.print')}
              </Button>
            </Stack>
          </Stack>
        )}
        <Alert severity="info">{t('iam:access_report.pdf_notice')}</Alert>
        <DataTable
          captionKey="iam:access_report.title"
          columns={columns}
          rows={report.data?.pages.flatMap((page) => page.items)}
          getRowId={(row) => row.eventId}
          isLoading={report.isPending}
          error={report.error}
          onRetry={() => void report.refetch()}
          hasMore={report.hasNextPage}
          onLoadMore={() => void report.fetchNextPage()}
          isFetchingMore={report.isFetchingNextPage}
          emptyTitleKey="iam:access_report.empty"
        />
        {exportError ? <ErrorState error={exportError} compact /> : null}
      </Stack>
      <Snackbar open={notice !== null} autoHideDuration={4000} onClose={() => setNotice(null)} message={notice} />
    </ListPage>
  );
}
